import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  background: var(--carbon-gradient);
  border-bottom: 3px solid var(--racing-red);
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  box-shadow: var(--shadow-carbon);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  
  @media (max-width: 768px) {
    padding: 0 var(--space-4);
    height: 70px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-2xl);
  font-weight: var(--font-black);
  color: var(--white);
  text-decoration: none;
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--racing-red);
    text-decoration: none;
    transform: scale(1.02);
  }
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--racing-gradient);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-lg);
  font-weight: var(--font-black);
  color: var(--white);
  box-shadow: var(--shadow-racing);
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: var(--text-base);
  }
`;

const LogoText = styled.span`
  background: linear-gradient(135deg, var(--white) 0%, var(--racing-red) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const Nav = styled.nav<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-8);
  
  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: var(--carbon-gradient);
    flex-direction: column;
    padding: var(--space-6);
    gap: var(--space-4);
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    transition: transform var(--transition-normal);
    border-bottom: 3px solid var(--racing-red);
    box-shadow: var(--shadow-carbon);
  }
`;

const NavLink = styled(Link)<{ isActive: boolean }>`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: ${props => props.isActive ? 'var(--racing-red)' : 'var(--white)'};
  text-decoration: none;
  position: relative;
  transition: all var(--transition-fast);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${props => props.isActive ? '100%' : '0'};
    height: 2px;
    background: var(--racing-gradient);
    transition: width var(--transition-fast);
  }
  
  &:hover {
    color: var(--racing-red);
    text-decoration: none;
    transform: translateY(-2px);
    
    &::after {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    font-size: var(--text-xl);
    padding: var(--space-2) 0;
    width: 100%;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2);
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MenuLine = styled.span<{ isOpen: boolean; index: number }>`
  width: 25px;
  height: 3px;
  background: var(--white);
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
  transform-origin: center;
  
  ${props => props.isOpen && props.index === 0 && `
    transform: rotate(45deg) translate(6px, 6px);
  `}
  
  ${props => props.isOpen && props.index === 1 && `
    opacity: 0;
  `}
  
  ${props => props.isOpen && props.index === 2 && `
    transform: rotate(-45deg) translate(6px, -6px);
  `}
`;

const Tagline = styled.div`
  font-size: var(--text-sm);
  color: var(--chrome-silver);
  font-weight: var(--font-medium);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <LogoIcon>🏁</LogoIcon>
          <div>
            <LogoText>CarBuzz.fit</LogoText>
            <Tagline>Automotive Excellence</Tagline>
          </div>
        </Logo>

        <Nav isOpen={isMenuOpen}>
          <NavLink 
            to="/" 
            isActive={isActive('/')}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </NavLink>
          <NavLink 
            to="/about" 
            isActive={isActive('/about')}
            onClick={() => setIsMenuOpen(false)}
          >
            About
          </NavLink>
          <NavLink 
            to="/quiz" 
            isActive={isActive('/quiz')}
            onClick={() => setIsMenuOpen(false)}
          >
            Quiz
          </NavLink>
          <NavLink 
            to="/privacy" 
            isActive={isActive('/privacy')}
            onClick={() => setIsMenuOpen(false)}
          >
            Privacy
          </NavLink>
          <NavLink 
            to="/terms" 
            isActive={isActive('/terms')}
            onClick={() => setIsMenuOpen(false)}
          >
            Terms
          </NavLink>
        </Nav>

        <MobileMenuButton onClick={toggleMenu}>
          <MenuLine isOpen={isMenuOpen} index={0} />
          <MenuLine isOpen={isMenuOpen} index={1} />
          <MenuLine isOpen={isMenuOpen} index={2} />
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
